
import { useState, useEffect, useRef } from "react";
import { base44 } from "@/api/base44Client";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Upload, CheckCircle, X, Users, ScanLine, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { logAttendanceScan, logAttendanceScanBulk } from "@/lib/attendanceScanLog";

export default function ASDAttendance() {
  const [bulkAbsentIds, setBulkAbsentIds] = useState("");
  const [bulkPresentIds, setBulkPresentIds] = useState("");
  const [bulkRefusalIds, setBulkRefusalIds] = useState("");
  const [showRefusalConfirm, setShowRefusalConfirm] = useState(false);
  const [message, setMessage] = useState(null);
  const [scanInput, setScanInput] = useState("");
  const [scanHistory, setScanHistory] = useState([]);
  const [welcomeBanner, setWelcomeBanner] = useState(null); // { name, otherId } | null
  const scanInputRef = useRef(null);
  const welcomeTimerRef = useRef(null);
  const queryClient = useQueryClient();

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });
  // Coach role: sees ONLY the Present option (Scan + Bulk Present tabs).
  // Absent, Excused, and Refusal tabs must be completely hidden — not
  // grayed-out — for Coaches.
  const isCoach = currentUser?.role === 'user' && currentUser?.user_type === 'coach';

  const { data: rosterEntries = [] } = useQuery({
    queryKey: ['rosterEntries'],
    queryFn: () => base44.entities.RosterEntry.list()
  });

  // Filter for ASD roster only (status === 'asd')
  const asdEntries = rosterEntries.filter((e) => e.status === 'asd');

  // Barcode / rapid single-scan mark-present — mirrors the EA Attendance flow
  // (Attendance.jsx `scanPresentMutation`) but targets ASD entries. Marks the
  // matched ASD entry `attendance_status='present'` (keeping `status='asd'`
  // per ASD closeout rules) and advances any active EA makeup tracking, so
  // showing up for ASD still burns down makeup days like it does for EA.
  const scanPresentMutation = useMutation({
    mutationFn: async (rawId) => {
      const normalizeId = (id) => id.toString().trim().replace(/^0+/, '') || '0';
      const scanned = rawId.toString().trim();
      if (!scanned) throw new Error("Empty scan");

      const normalizedInput = normalizeId(scanned);
      const entries = asdEntries.filter((e) => {
        const normalizedDbId = normalizeId(e.other_id || '');
        return normalizedDbId === normalizedInput && e.attendance_status !== 'present';
      });

      if (entries.length === 0) {
        throw new Error(`No ASD entry to check in for ID ${scanned}`);
      }

      const studentName = `${entries[0].student_first_name} ${entries[0].student_last_name}`;

      for (const entry of entries) {
        await base44.entities.RosterEntry.update(entry.id, {
          attendance_status: 'present',
          status: 'asd',
        });
        await base44.entities.AttendanceLog.create({
          student_id: entry.other_id,
          student_name: studentName,
          roster_entry_id: entry.id,
          log_date: format(new Date(), 'yyyy-MM-dd'),
          status: 'present',
          scanned: true,
          method: 'barcode_scan_asd',
        });
        logAttendanceScan({
          studentId: entry.other_id,
          studentName,
          rosterType: 'asd',
          status: 'present',
          method: 'barcode_scan',
        });
      }

      // Advance makeup tracking if applicable (identical to EA behavior)
      try {
        const tracking = await base44.entities.EAMissTracking.filter({
          student_id: entries[0].other_id,
          status: 'active',
        });
        for (const t of tracking) {
          const remaining = (t.makeup_days_owed || 0) - (t.makeup_days_completed || 0);
          if (remaining > 0) {
            const newCompleted = (t.makeup_days_completed || 0) + 1;
            let makeupDates = [];
            try { makeupDates = t.makeup_dates ? JSON.parse(t.makeup_dates) : []; } catch (e) { makeupDates = []; }
            makeupDates.push(format(new Date(), 'yyyy-MM-dd'));
            const allComplete = newCompleted >= (t.makeup_days_owed || 0);
            await base44.entities.EAMissTracking.update(t.id, {
              makeup_days_completed: newCompleted,
              makeup_dates: JSON.stringify(makeupDates),
              status: allComplete ? 'resolved' : 'active',
            });
          }
        }
      } catch (e) {
        console.warn("Makeup tracking update skipped:", e);
      }

      return { studentName, otherId: scanned, count: entries.length };
    },
    onSuccess: ({ studentName, otherId, count }) => {
      setScanHistory((prev) => [
        { ts: new Date(), otherId, studentName, status: 'ok', count },
        ...prev.slice(0, 19),
      ]);
      setWelcomeBanner({ name: studentName, otherId });
      if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
      welcomeTimerRef.current = setTimeout(() => setWelcomeBanner(null), 2000);
      queryClient.invalidateQueries(['rosterEntries']);
      queryClient.invalidateQueries(['eaMissTracking']);
    },
    onError: (err, rawId) => {
      setScanHistory((prev) => [
        { ts: new Date(), otherId: rawId, studentName: null, status: 'error', message: err.message },
        ...prev.slice(0, 19),
      ]);
      setWelcomeBanner({ name: null, otherId: rawId, error: err.message });
      if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
      welcomeTimerRef.current = setTimeout(() => setWelcomeBanner(null), 2000);
    },
  });

  const handleScanSubmit = (e) => {
    e.preventDefault();
    const value = scanInput.trim();
    if (!value) return;
    scanPresentMutation.mutate(value);
    setScanInput("");
  };

  // Auto-submit only when EXACTLY 6 digits have been entered. Barcode scanners
  // still push all 6 characters in one burst so the debounce is instant for
  // them; manual typing no longer fires after the first digit.
  useEffect(() => {
    const trimmed = scanInput.trim();
    if (!trimmed) return;
    if (scanPresentMutation.isPending) return;
    if (!/^\d{6}$/.test(trimmed)) return;
    const t = setTimeout(() => {
      scanPresentMutation.mutate(trimmed);
      setScanInput("");
    }, 100);
    return () => clearTimeout(t);
  }, [scanInput, scanPresentMutation]);

  // Clean up the welcome timer on unmount
  useEffect(() => {
    return () => {
      if (welcomeTimerRef.current) clearTimeout(welcomeTimerRef.current);
    };
  }, []);

  // Refocus scan input once welcome banner clears
  useEffect(() => {
    if (welcomeBanner) return;
    scanInputRef.current?.focus();
  }, [welcomeBanner]);

  const markPresentBulkMutation = useMutation({
    mutationFn: async (otherIds) => {
      const ids = otherIds.split('\n').map((id) => id.trim()).filter((id) => id);
      let markedCount = 0;
      let namesMarked = new Set();

      // Helper function to normalize IDs (remove leading zeros)
      const normalizeId = (id) => {
        // Ensure id is treated as a string before trimming and replacing
        const idStr = String(id).trim();
        // Remove leading zeros, but if the string is all zeros, keep one '0'
        return idStr.replace(/^0+/, '') || (idStr === '0' ? '0' : ''); // Handles case where input is '0' or ''
      };

      for (const otherId of ids) {
        const normalizedInput = normalizeId(otherId);
        
        // Match by normalized Other ID (removing leading zeros from both sides)
        const entries = asdEntries.filter((e) => {
          const normalizedDbId = normalizeId(e.other_id || '');
          return normalizedDbId === normalizedInput;
        });

        for (const entry of entries) {
          await base44.entities.RosterEntry.update(entry.id, {
            attendance_status: 'present'
          });

          await base44.entities.AttendanceLog.create({
            student_id: entry.other_id,
            student_name: `${entry.student_first_name} ${entry.student_last_name}`,
            roster_entry_id: entry.id,
            log_date: format(new Date(), 'yyyy-MM-dd'),
            status: 'present',
            scanned: false,
            method: 'asd_present'
          });
          logAttendanceScan({
            studentId: entry.other_id,
            studentName: `${entry.student_first_name} ${entry.student_last_name}`,
            rosterType: 'asd',
            status: 'present',
            method: 'manual_entry',
          });

          markedCount++;
          namesMarked.add(`${entry.student_first_name} ${entry.student_last_name}`);
        }
      }

      return { count: markedCount, names: Array.from(namesMarked) };
    },
    onSuccess: ({ count, names }) => {
      let successMessage = `${count} ASD assignment(s) marked as PRESENT.`;
      if (names.length > 0) {
        successMessage += ` Students: ${names.join(', ')}`;
      }
      setMessage({ type: 'success', text: successMessage });
      setBulkPresentIds("");
      queryClient.invalidateQueries(['rosterEntries']);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || "Failed to mark students present" });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const markAbsentBulkMutation = useMutation({
    mutationFn: async (otherIds) => {
      const ids = otherIds.split('\n').map((id) => id.trim()).filter((id) => id);
      let markedCount = 0;
      let namesMarked = new Set();

      // Helper function to normalize IDs (remove leading zeros)
      const normalizeId = (id) => {
        // Ensure id is treated as a string before trimming and replacing
        const idStr = String(id).trim();
        // Remove leading zeros, but if the string is all zeros, keep one '0'
        return idStr.replace(/^0+/, '') || (idStr === '0' ? '0' : '');
      };

      for (const otherId of ids) {
        const normalizedInput = normalizeId(otherId);
        
        // Match by normalized Other ID
        const entries = asdEntries.filter((e) => {
          const normalizedDbId = normalizeId(e.other_id || '');
          return normalizedDbId === normalizedInput;
        });

        for (const entry of entries) {
          await base44.entities.RosterEntry.update(entry.id, {
            attendance_status: 'absent'
          });

          await base44.entities.AttendanceLog.create({
            student_id: entry.other_id,
            student_name: `${entry.student_first_name} ${entry.student_last_name}`,
            roster_entry_id: entry.id,
            log_date: format(new Date(), 'yyyy-MM-dd'),
            status: 'absent',
            scanned: false,
            method: 'asd_absent'
          });
          logAttendanceScan({
            studentId: entry.other_id,
            studentName: `${entry.student_first_name} ${entry.student_last_name}`,
            rosterType: 'asd',
            status: 'absent',
            method: 'manual_entry',
          });

          markedCount++;
          namesMarked.add(`${entry.student_first_name} ${entry.student_last_name}`);
        }
      }

      return { count: markedCount, names: Array.from(namesMarked) };
    },
    onSuccess: ({ count, names }) => {
      let successMessage = `${count} ASD assignment(s) marked as ABSENT.`;
      if (names.length > 0) {
        successMessage += ` Students: ${names.join(', ')}`;
      }
      setMessage({ type: 'success', text: successMessage });
      setBulkAbsentIds("");
      queryClient.invalidateQueries(['rosterEntries']);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || "Failed to mark students absent" });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  // Bulk Refusal — mirrors EA "Process Refusals": marks every ASD roster entry
  // currently in `pending` state as `refusal` in one shot. No individual IDs
  // needed. Individual P/A/E/R paths are unaffected.
  const processAsdRefusalsMutation = useMutation({
    mutationFn: async () => {
      const pendingEntries = asdEntries.filter((e) => (e.attendance_status || 'pending') === 'pending');
      if (pendingEntries.length === 0) return { count: 0, names: [] };

      const namesMarked = new Set();
      const logsToCreate = pendingEntries.map((entry) => {
        namesMarked.add(`${entry.student_first_name} ${entry.student_last_name}`);
        return {
          student_id: entry.other_id,
          student_name: `${entry.student_first_name} ${entry.student_last_name}`,
          roster_entry_id: entry.id,
          log_date: format(new Date(), 'yyyy-MM-dd'),
          status: 'refusal',
          scanned: false,
          method: 'asd_refusal_bulk',
        };
      });

      const batchSize = 3;
      const entryIds = pendingEntries.map((e) => e.id);
      for (let i = 0; i < entryIds.length; i += batchSize) {
        const batch = entryIds.slice(i, i + batchSize);
        await Promise.all(batch.map((id) =>
          base44.entities.RosterEntry.update(id, { attendance_status: 'refusal' })
        ));
        await new Promise((r) => setTimeout(r, 1000));
      }

      // Chunked AttendanceLog inserts
      const logChunkSize = 50;
      for (let i = 0; i < logsToCreate.length; i += logChunkSize) {
        const chunk = logsToCreate.slice(i, i + logChunkSize);
        await base44.entities.AttendanceLog.bulkCreate(chunk);
        if (i + logChunkSize < logsToCreate.length) {
          await new Promise((r) => setTimeout(r, 500));
        }
      }

      // Mirror to AttendanceScanLog (audit-only)
      logAttendanceScanBulk(logsToCreate.map((l) => ({
        studentId: l.student_id,
        studentName: l.student_name,
        rosterType: 'asd',
        status: 'refusal',
        method: 'manual_entry',
      })));

      return { count: pendingEntries.length, names: Array.from(namesMarked) };
    },
    onSuccess: ({ count, names }) => {
      setShowRefusalConfirm(false);
      const successMessage =
        count === 0
          ? 'No pending ASD students to mark.'
          : `${count} ASD assignment(s) marked as REFUSAL. They remain on the ASD roster until closeout.${
              names.length ? ` Students: ${names.join(', ')}` : ''
            }`;
      setMessage({ type: 'success', text: successMessage });
      queryClient.invalidateQueries(['rosterEntries']);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setShowRefusalConfirm(false);
      setMessage({ type: 'error', text: err.message || 'Failed to bulk-mark refusals' });
      setTimeout(() => setMessage(null), 5000);
    },
  });

  // Bulk Refusal — legacy pasted-ID mutation. Kept intact but no longer wired
  // into the UI; the new confirmation-based flow above supersedes it.
  const markRefusalBulkMutation = useMutation({
    mutationFn: async (otherIds) => {
      const ids = otherIds.split('\n').map((id) => id.trim()).filter((id) => id);
      let markedCount = 0;
      let namesMarked = new Set();

      const normalizeId = (id) => {
        const idStr = String(id).trim();
        return idStr.replace(/^0+/, '') || (idStr === '0' ? '0' : '');
      };

      for (const otherId of ids) {
        const normalizedInput = normalizeId(otherId);
        const entries = asdEntries.filter((e) => {
          const normalizedDbId = normalizeId(e.other_id || '');
          return normalizedDbId === normalizedInput;
        });
        for (const entry of entries) {
          await base44.entities.RosterEntry.update(entry.id, {
            attendance_status: 'refusal'
          });
          await base44.entities.AttendanceLog.create({
            student_id: entry.other_id,
            student_name: `${entry.student_first_name} ${entry.student_last_name}`,
            roster_entry_id: entry.id,
            log_date: format(new Date(), 'yyyy-MM-dd'),
            status: 'refusal',
            scanned: false,
            method: 'asd_refusal'
          });
          logAttendanceScan({
            studentId: entry.other_id,
            studentName: `${entry.student_first_name} ${entry.student_last_name}`,
            rosterType: 'asd',
            status: 'refusal',
            method: 'manual_entry',
          });
          markedCount++;
          namesMarked.add(`${entry.student_first_name} ${entry.student_last_name}`);
        }
      }
      return { count: markedCount, names: Array.from(namesMarked) };
    },
    onSuccess: ({ count, names }) => {
      let successMessage = `${count} ASD assignment(s) marked as REFUSAL.`;
      if (names.length > 0) successMessage += ` Students: ${names.join(', ')}`;
      setMessage({ type: 'success', text: successMessage });
      setBulkRefusalIds("");
      queryClient.invalidateQueries(['rosterEntries']);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || 'Failed to mark students refusal' });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  // Count unique students (not assignments) for live display
  const asdPendingStudents = new Set(asdEntries.map(e => e.other_id));
  const asdPresentStudents = new Set(asdEntries.filter((e) => e.attendance_status === 'present').map(e => e.other_id));
  const asdPendingCount = asdPendingStudents.size;
  const asdPresentCount = asdPresentStudents.size;

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">ASD Attendance</h1>
          <p className="text-slate-600 mt-1">Mark After School Detention Attendance</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Card className="border-none shadow-lg bg-gradient-to-br from-red-600 to-red-700 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">ASD Total</p>
                  <p className="text-3xl font-bold">{asdPendingCount}</p>
                </div>
                <AlertTriangle className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-none shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">ASD Present Today</p>
                  <p className="text-3xl font-bold">{asdPresentCount}</p>
                </div>
                <CheckCircle className="w-10 h-10 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {message &&
        <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ?
          <CheckCircle className="w-4 h-4 text-green-600" /> :

          <X className="w-4 h-4 text-red-600" />
          }
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        }

        <Tabs defaultValue="scan" className="w-full">
          <TabsList className={`grid w-full ${isCoach ? 'grid-cols-2' : 'grid-cols-4'}`}>
            <TabsTrigger value="scan" data-testid="tab-scan">
              <ScanLine className="w-4 h-4 mr-1" />
              Scan
            </TabsTrigger>
            <TabsTrigger value="bulk-present">Bulk Present</TabsTrigger>
            {!isCoach && <TabsTrigger value="bulk-absent">Bulk Absent</TabsTrigger>}
            {!isCoach && (
              <TabsTrigger value="bulk-refusal" data-testid="tab-bulk-refusal">Bulk Refusal</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="scan">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ScanLine className="w-5 h-5" />
                  Barcode Scanner Check-In
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-sm text-blue-900">
                    <strong>Scanner Instructions:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Point your USB / Bluetooth barcode scanner at a student ID badge</li>
                      <li>The scanned ID appears in the field and auto-submits at 6 digits</li>
                      <li>The student is instantly marked <strong>PRESENT</strong> on the ASD roster and makeup days advance</li>
                      <li>You can also type a 6-digit ID manually — it auto-submits once complete, or press Enter</li>
                    </ul>
                  </AlertDescription>
                </Alert>
                <form onSubmit={handleScanSubmit} className="flex gap-2">
                  <Input
                    ref={scanInputRef}
                    data-testid="asd-scan-input"
                    autoFocus
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    onBlur={() => setTimeout(() => scanInputRef.current?.focus(), 100)}
                    placeholder={welcomeBanner ? "" : "Ready to scan — point scanner at student ID badge"}
                    className="h-14 text-lg font-mono"
                    disabled={scanPresentMutation.isPending || !!welcomeBanner}
                    maxLength={8}
                  />
                  <Button
                    type="submit"
                    data-testid="asd-scan-submit-button"
                    className="h-14 px-6 bg-green-600 hover:bg-green-700"
                    disabled={!scanInput.trim() || scanPresentMutation.isPending}
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Mark Present
                  </Button>
                </form>

                {welcomeBanner && welcomeBanner.name && (
                  <div
                    data-testid="asd-scan-welcome-banner"
                    className="rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 text-white p-8 shadow-2xl animate-in fade-in zoom-in duration-200"
                  >
                    <div className="flex items-center gap-4">
                      <CheckCircle className="w-16 h-16 flex-shrink-0" strokeWidth={2.5} />
                      <div>
                        <p className="text-4xl font-bold tracking-tight">
                          Welcome, {welcomeBanner.name}!
                        </p>
                        <p className="text-lg opacity-90 mt-1 font-mono">
                          ID {welcomeBanner.otherId} · Marked Present
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {welcomeBanner && welcomeBanner.error && (
                  <div
                    data-testid="asd-scan-error-banner"
                    className="rounded-2xl bg-gradient-to-br from-red-500 to-red-700 text-white p-8 shadow-2xl animate-in fade-in zoom-in duration-200"
                  >
                    <div className="flex items-center gap-4">
                      <AlertCircle className="w-16 h-16 flex-shrink-0" strokeWidth={2.5} />
                      <div>
                        <p className="text-3xl font-bold tracking-tight">Scan Failed</p>
                        <p className="text-lg opacity-90 mt-1">{welcomeBanner.error}</p>
                      </div>
                    </div>
                  </div>
                )}

                {!welcomeBanner && !scanPresentMutation.isPending && (
                  <div
                    data-testid="asd-scan-ready-state"
                    className="rounded-lg border-2 border-dashed border-blue-300 bg-blue-50/50 p-6 text-center"
                  >
                    <ScanLine className="w-10 h-10 text-blue-500 mx-auto mb-2 animate-pulse" />
                    <p className="text-blue-900 font-medium">Ready to scan</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Point your scanner at a student badge — check-in happens automatically
                    </p>
                  </div>
                )}

                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-semibold text-slate-700 mb-2">
                    Recent Scans ({scanHistory.length})
                  </p>
                  {scanHistory.length === 0 ? (
                    <p className="text-sm text-slate-500">Scan a badge to see history here</p>
                  ) : (
                    <ul className="space-y-1 max-h-64 overflow-y-auto" data-testid="asd-scan-history-list">
                      {scanHistory.map((s, idx) => (
                        <li
                          key={idx}
                          data-testid={`asd-scan-history-item-${idx}`}
                          className={`text-sm flex items-center gap-2 p-2 rounded ${
                            s.status === 'ok' ? 'bg-green-100 text-green-900' : 'bg-red-100 text-red-900'
                          }`}
                        >
                          {s.status === 'ok' ? (
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          )}
                          <span className="font-mono text-xs">{format(s.ts, 'HH:mm:ss')}</span>
                          <span className="font-mono">{s.otherId}</span>
                          <span className="flex-1">
                            {s.status === 'ok' ? `→ ${s.studentName} marked Present` : `→ ${s.message}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bulk-present">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Bulk Mark Present
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription className="text-sm text-green-900">
                    <strong>📋 Bulk Present Instructions:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Paste <strong>Other IDs</strong> from another system</li>
                      <li>One ID per line</li>
                      <li>All ASD students will be marked as PRESENT</li>
                    </ul>
                  </AlertDescription>
                </Alert>
                <div>
                  <Textarea
                    value={bulkPresentIds}
                    onChange={(e) => setBulkPresentIds(e.target.value)}
                    placeholder="Enter Other IDs, one per line..."
                    className="h-64 font-mono" />

                  <p className="text-sm text-slate-500 mt-2">
                    Paste or type <strong>Other IDs</strong>, each on a new line
                  </p>
                </div>
                <Button
                  onClick={() => markPresentBulkMutation.mutate(bulkPresentIds)}
                  className="w-full bg-green-600 hover:bg-green-700 h-12"
                  disabled={!bulkPresentIds.trim() || markPresentBulkMutation.isPending}>

                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark All as Present
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {!isCoach && (
          <TabsContent value="bulk-absent">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Bulk Mark Absent
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertDescription className="text-sm text-yellow-900">
                    <strong>📋 Bulk Absent Instructions:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Paste <strong>Other IDs</strong> of absent students</li>
                      <li>One ID per line</li>
                      <li>All ASD students will be marked as ABSENT</li>
                    </ul>
                  </AlertDescription>
                </Alert>
                <div>
                  <Textarea
                    value={bulkAbsentIds}
                    onChange={(e) => setBulkAbsentIds(e.target.value)}
                    placeholder="Enter Other IDs, one per line..."
                    className="h-64 font-mono" />

                  <p className="text-sm text-slate-500 mt-2">
                    Paste or type <strong>Other IDs</strong>, each on a new line
                  </p>
                </div>
                <Button
                  onClick={() => markAbsentBulkMutation.mutate(bulkAbsentIds)}
                  className="w-full bg-yellow-600 hover:bg-yellow-700 h-12"
                  disabled={!bulkAbsentIds.trim() || markAbsentBulkMutation.isPending}>

                  Mark All as Absent
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {!isCoach && (
          <TabsContent value="bulk-refusal">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Bulk Mark Refusal
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-red-50 border-red-200">
                  <AlertDescription className="text-sm text-red-900">
                    <strong>Bulk Refusal:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>Every ASD student still in <strong>Pending</strong> will be flipped to <strong>Refusal (R)</strong> in one click.</li>
                      <li>Students already marked P, A, E, or R are <strong>not</strong> touched.</li>
                      <li>Students remain on the ASD roster until makeup 100% + consequence resolved.</li>
                      <li>A confirmation dialog appears before anything changes.</li>
                    </ul>
                  </AlertDescription>
                </Alert>

                {(() => {
                  const pendingCount = asdEntries.filter((e) => (e.attendance_status || 'pending') === 'pending').length;
                  return (
                    <div className="p-6 rounded-lg border-2 border-dashed border-red-300 bg-red-50/60 text-center">
                      <p className="text-sm text-red-900 mb-1">Currently in Pending on ASD</p>
                      <p className="text-4xl font-extrabold text-red-800" data-testid="asd-pending-count">
                        {pendingCount}
                      </p>
                      <p className="text-xs text-red-700 mt-1">student(s) will be marked Refusal</p>
                    </div>
                  );
                })()}

                <Button
                  onClick={() => setShowRefusalConfirm(true)}
                  className="w-full bg-red-600 hover:bg-red-700 h-12"
                  disabled={
                    processAsdRefusalsMutation.isPending ||
                    asdEntries.filter((e) => (e.attendance_status || 'pending') === 'pending').length === 0
                  }
                  data-testid="bulk-refusal-open-confirm"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {processAsdRefusalsMutation.isPending
                    ? 'Processing...'
                    : 'Process All Pending → Refusal'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          )}
        </Tabs>

        {/* Confirmation dialog for bulk refusal */}
        <Dialog open={showRefusalConfirm} onOpenChange={setShowRefusalConfirm}>
          <DialogContent className="max-w-md" data-testid="bulk-refusal-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-5 h-5" />
                Confirm Bulk Refusal
              </DialogTitle>
              <DialogDescription>
                This will mark <strong>{
                  asdEntries.filter((e) => (e.attendance_status || 'pending') === 'pending').length
                }</strong> pending ASD student(s) as <strong>Refusal (R)</strong>.
                Students already marked P, A, E, or R will not be changed. This action cannot be undone in bulk.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowRefusalConfirm(false)}
                disabled={processAsdRefusalsMutation.isPending}
                data-testid="bulk-refusal-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => processAsdRefusalsMutation.mutate()}
                className="bg-red-700 hover:bg-red-800 text-white"
                disabled={processAsdRefusalsMutation.isPending}
                data-testid="bulk-refusal-confirm"
              >
                {processAsdRefusalsMutation.isPending
                  ? 'Processing...'
                  : 'Yes, Mark All Pending as Refusal'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>);

}
